import type { EnterApiHttpEvents, HttpRetryEvent } from '../config';
import { EnterHttpError } from './errors';
import type { HttpMethod, HttpRequestOptions } from './types';

/** 共享默认只吸收服务端暂时不可用；429 必须由具体端点显式开启。 */
const DEFAULT_RETRYABLE_STATUSES = new Set([503, 504]);
const MAX_RETRY_AFTER_ATTEMPTS = 5;
const MAX_RETRY_AFTER_MS = 60_000;
const DEFAULT_RETRY_DELAY_MS = 5_000;

export type HttpExecutor = <T>() => Promise<T>;

export type RetryAfterWait = (delayMs: number, signal?: AbortSignal) => Promise<void>;

export interface RetryAfterRequest {
    path: string;
    method: HttpMethod;
    retry?: HttpRequestOptions['retry'];
    retryStatuses?: HttpRequestOptions['retryStatuses'];
    retryOnError?: HttpRequestOptions['retryOnError'];
    hasIdempotencyKey: boolean;
    signal?: AbortSignal;
}

/** 将 Retry-After 的秒数或 HTTP 日期转换为等待毫秒数。 */
export function parseRetryAfterMs(
    value: string | null | undefined,
    nowMs = Date.now()
): number | null {
    if (!value) {
        return null;
    }

    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0) {
        return seconds * 1000;
    }

    const retryAtMs = Date.parse(value);
    if (Number.isFinite(retryAtMs)) {
        return Math.max(0, retryAtMs - nowMs);
    }

    return null;
}

function isRetryableRequest(request: RetryAfterRequest): boolean {
    if (request.retry === 'never') {
        return false;
    }
    if (request.method === 'GET') {
        return true;
    }
    return request.retry === 'idempotent' && request.hasIdempotencyKey;
}

function waitForRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(signal.reason ?? new Error('Request aborted'));
            return;
        }

        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, delayMs);
        const onAbort = () => {
            clearTimeout(timer);
            reject(signal?.reason ?? new Error('Request aborted'));
        };
        signal?.addEventListener('abort', onAbort, { once: true });
    });
}

async function notifySafely(callback: (() => Promise<void>) | undefined): Promise<void> {
    try {
        await callback?.();
    } catch {
        // 宿主观察回调失败时不能覆盖原始传输结果。
    }
}

/**
 * 给一个已准备好的传输请求增加服务端 Retry-After 语义。
 * executor 刻意不接收参数，确保每次重试复用原始 headers、body、token 和 abort signal。
 */
export function withRetryAfter(
    execute: HttpExecutor,
    request: RetryAfterRequest,
    events?: Pick<EnterApiHttpEvents, 'onRetryAfter' | 'onRetryExhausted'>,
    wait: RetryAfterWait = waitForRetry
): HttpExecutor {
    const retryableStatuses = request.retryStatuses
        ? new Set(request.retryStatuses)
        : DEFAULT_RETRYABLE_STATUSES;

    return async <T>() => {
        for (let attempt = 0; ; attempt += 1) {
            if (request.signal?.aborted) {
                throw request.signal.reason ?? new Error('Request aborted');
            }
            try {
                return await execute<T>();
            } catch (error) {
                if (request.signal?.aborted || !(error instanceof EnterHttpError)) {
                    throw error;
                }

                const serverDelayMs = parseRetryAfterMs(error.headers?.get('retry-after'));
                const retryAfterMs = request.retryOnError && (serverDelayMs === null || serverDelayMs <= 0)
                    ? DEFAULT_RETRY_DELAY_MS
                    : serverDelayMs;
                const status = error.status ?? 0;
                const retryable =
                    (request.retryOnError || retryableStatuses.has(status)) &&
                    retryAfterMs !== null &&
                    retryAfterMs > 0 &&
                    retryAfterMs <= MAX_RETRY_AFTER_MS &&
                    isRetryableRequest(request);

                if (retryable && attempt < MAX_RETRY_AFTER_ATTEMPTS) {
                    const event: HttpRetryEvent = {
                        path: request.path,
                        method: request.method,
                        status: error.status,
                        attempt: attempt + 1,
                        maxAttempts: MAX_RETRY_AFTER_ATTEMPTS,
                        delayMs: retryAfterMs,
                    };
                    await notifySafely(() => Promise.resolve(events?.onRetryAfter?.(event)));
                    await wait(retryAfterMs, request.signal);
                    continue;
                }

                if (retryable) {
                    await notifySafely(() =>
                        Promise.resolve(
                            events?.onRetryExhausted?.({
                                path: request.path,
                                method: request.method,
                                status: error.status,
                                maxAttempts: MAX_RETRY_AFTER_ATTEMPTS,
                            })
                        )
                    );
                }
                throw error;
            }
        }
    };
}
